import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import { CustomProvider, DateRangePicker } from 'rsuite';
import sv from 'rsuite/locales/sv_SE';
import en from 'rsuite/locales/en_GB';
import BaseTable, { IBaseTableData, IBaseTableRow } from '../table/baseTable';
import { HistoryDataItem, ShipsDataItem } from './interfaces/arrivalsDepartures.interfaces';
import { useShipVisitHistoryData } from './hooks/useShipVisitHistoryData';
import StandardButton from '../btn-standard';
import LoadMoreButton from '../btnLoadMore';

export interface ShipVisitHistoryProps {
  show: boolean;
  selectedShip: ShipsDataItem | null;
  imo: string | null;
  locale: string;
  onGoBack: Function;
}

export interface ExpandedHistoryDetailsProps {
  historyData: HistoryDataItem;
  expanded: boolean;
}

// ------------------------------------------------------------------------------------------
//  Headers, Columns - for the table
// ------------------------------------------------------------------------------------------

const TableHeader = (headerText: string, className?: string) => <span className={className}>{headerText}</span>;
const RowColumn = (columnText: string) => <div className="arrivals-departures-column">{columnText}</div>;
const TimeColumn = (children: JSX.Element) => <div className="arrivals-departures-column time ">{children}</div>;

// ------------------------------------------------------------------------------------------
//
// SHIP VISIT HISTORY COMPONENT
//
// ------------------------------------------------------------------------------------------

const ShipVisitHistory = ({ show, selectedShip, locale, onGoBack, imo }: ShipVisitHistoryProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dateSpan, setDateSpan] = useState<Date[]>();
  const [loadMore, setLoadMore] = useState(false);

  // Load history data hook
  const { historyList, noMoreToLoad } = useShipVisitHistoryData({ imoNumber: imo, loadMore, dateSpan });
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // ------------------------------------------------------------------------------------------
  // Translation/Locale
  // ------------------------------------------------------------------------------------------

  useEffect(() => {
    if (locale) {
      dayjs.locale(locale);
    }
  }, [locale]);

  const { t } = useTranslation();

  // ------------------------------------------------------------------------------------------
  // getTimeInQuay - creates time in quay string for the table
  // ------------------------------------------------------------------------------------------

  const getTimeInQuay = (shipHistoryData: HistoryDataItem | null) => {
    const daysStr =
      shipHistoryData?.daysInHarbour && shipHistoryData?.daysInHarbour > 0 ? shipHistoryData?.daysInHarbour : null;
    const hoursStr = shipHistoryData?.hoursInHarbour || null;

    if (daysStr && hoursStr) {
      return `${daysStr}${t('arrivalsdepartures.daysShort')} ${hoursStr}${t('arrivalsdepartures.hoursShort')}`;
    }

    if (hoursStr) {
      return `${hoursStr}${t('arrivalsdepartures.hoursShort')}`;
    }

    if (daysStr) {
      return `${daysStr}${t('arrivalsdepartures.daysShort')}`;
    }

    return '-';
  };

  // ------------------------------------------------------------------------------------------
  // getHoursInQuay - calculates amount of hours the ship as been in quay for sorting reasons
  // ------------------------------------------------------------------------------------------

  const getHoursInQuay = (shipHistoryData: HistoryDataItem | null) => {
    const daysInHours = (shipHistoryData?.daysInHarbour || 0) * 24;
    const hours = shipHistoryData?.hoursInHarbour || 0;
    return daysInHours + hours;
  };

  // ----------------------------------------------------------------------------
  //
  //  EXPANDED HISTORY DETAILS COMPONENT
  //
  // ----------------------------------------------------------------------------

  const ExpandedHistoryDetails = ({ expanded, historyData }: ExpandedHistoryDetailsProps) => {
    return (
      <div className="arrivals-departures-ship-details">
        <div className="arrivals-departures-ship-details-content">
          <div className="arrivals-departures-ship-details-top">
            <div className="column-left">
              <ul>
                <li>
                  <h6>{t('arrivalsdepartures.arrivedFrom')}:</h6>
                  <p>{`${historyData?.prevPortName || '-'}`}</p>
                </li>
                <li>
                  <h6>{t('arrivalsdepartures.departuredTo')}:</h6>
                  <p>{`${historyData?.nextPortName || '-'}`}</p>
                </li>
              </ul>
            </div>
            <div className="column-right">
              <ul>
                <li>
                  <h6>{t('arrivalsdepartures.agentName')}:</h6>
                  <p>{historyData?.agentName || '-'}</p>
                </li>
                <li>
                  <h6>{t('arrivalsdepartures.timeInQuay')}:</h6>
                  <p>{getTimeInQuay(historyData) || '-'}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ------------------------------------------------------------------------------------------
  // tableData - useMemo - generates table data based on history data
  // ------------------------------------------------------------------------------------------

  const tableData = useMemo(() => {
    const baseData: IBaseTableData = {
      headers: [
        { id: 1, headerData: TableHeader(t('arrivalsdepartures.time')), columnId: 1, maxWidth: 150 },
        { id: 2, headerData: TableHeader(t('arrivalsdepartures.quayName')), columnId: 2 },
        { id: 3, headerData: TableHeader(t('arrivalsdepartures.arrivedFrom')), hideOnMobile: true, columnId: 3 },
        {
          id: 4,
          headerData: TableHeader(t('arrivalsdepartures.departuredTo')),
          hideOnMobile: true,
          columnId: 4,
        },
        {
          id: 5,
          headerData: TableHeader(t('arrivalsdepartures.agentName')),
          hideOnMobile: true,
          columnId: 5,
          maxWidth: 190,
        },
        {
          id: 6,
          headerData: TableHeader(t('arrivalsdepartures.timeInQuay')),
          hideOnMobile: true,
          columnId: 6,
          maxWidth: 100,
        },
      ],
      rows: [],
    };

    setLoadMore(false);

    historyList?.forEach((data, index) => {
      const rowData: IBaseTableRow = {
        id: index,
        columns: [
          {
            id: 1,
            columnData: TimeColumn(
              <div>
                {data.arrivalTime ? (
                  <div className="time-column-item">
                    <div className="date">{dayjs(data.arrivalTime).format('YYYY-MM-DD')}</div>
                    <div>{dayjs(data.arrivalTime).format('HH:mm')}</div>
                  </div>
                ) : null}
                {data.departureTime ? (
                  <div className="time-column-item">
                    <div className="date">{dayjs(data.departureTime).format('YYYY-MM-DD')}</div>
                    <div>{dayjs(data.departureTime).format('HH:mm')}</div>
                  </div>
                ) : null}
              </div>
            ),

            filterValue: dayjs().valueOf() - dayjs(data.arrivalTime).valueOf(),
            maxWidth: 150,
          },
          {
            id: 2,
            columnData: RowColumn(data.quayName.toLowerCase()),
            filterValue: data.quayName,
          },

          {
            id: 3,
            columnData: RowColumn(data.prevPortName),
            filterValue: data.prevPortName,
            hideOnMobile: true,
          },
          {
            id: 4,
            columnData: RowColumn(data.nextPortName),
            filterValue: data.nextPortName,
            hideOnMobile: true,
          },
          {
            id: 5,
            columnData: RowColumn(data.agentName),
            filterValue: data.agentName,
            hideOnMobile: true,
            maxWidth: 190,
          },
          {
            id: 6,
            columnData: RowColumn(getTimeInQuay(data)),
            filterValue: getHoursInQuay(data),
            hideOnMobile: true,
            maxWidth: 100,
          },
        ],
        expandedContentMobileOnly: true,
        expandedContent: <ExpandedHistoryDetails historyData={data} expanded={expandedRows.some((r) => r === index)} />,
        expandedAtStart: false,
      };

      baseData?.rows.push(rowData);
    });
    return baseData;
  }, [historyList, JSON.stringify(expandedRows)]);

  const changeDateSpan = (e: any) => {
    setDateSpan(e);
  };

  const onRowExpanded = (row: IBaseTableRow) => {
    expandedRows.push(row.id);
    setExpandedRows([...expandedRows]);
  };

  const onRowClosed = (row: IBaseTableRow) => {
    const i = expandedRows.findIndex((rowId) => rowId === row.id);
    if (i >= 0) {
      expandedRows.splice(i, 1);
      setExpandedRows([...expandedRows]);
    }
  };

  const loadMoreItems = () => {
    setLoadMore(true);
  };

  // ------------------------------------------------------------------------------------------
  // JSX
  // ------------------------------------------------------------------------------------------

  return show ? (
    <div ref={ref} className="arrivals-departures-history-table">
      <h3>{selectedShip?.name}</h3>
      <h4>{`${t('arrivalsdepartures.imoNumber')}: ${imo}`}</h4>

      <div className="arrivals-departures-top-bar">
        <StandardButton
          className="go-back-button"
          text={t('arrivalsdepartures.goBack')}
          isReversed
          onClick={() => onGoBack()}
          isWhite={false}
        />

        <CustomProvider locale={locale === 'sv' ? sv : en}>
          <DateRangePicker
            appearance="default"
            placeholder={t('arrivalsdepartures.date')}
            onChange={(e) => {
              changeDateSpan(e);
            }}
            isoWeek
          />
        </CustomProvider>
      </div>

      {historyList?.length ? (
        <BaseTable
          data={tableData}
          onExpanded={(row: IBaseTableRow) => onRowExpanded(row)}
          onClosed={(row: IBaseTableRow) => onRowClosed(row)}
          hideLoader={false}
        />
      ) : null}
      {historyList?.length === 0 ? <p className="history-data-missing">{t('arrivalsdepartures.dataMissing')}</p> : null}
      {historyList && historyList.length > 0 && !noMoreToLoad ? (
        <div className="arrivals-departures-load-more">
          <LoadMoreButton
            className="arrivals-departures-load-more-button"
            text={loadMore ? 'Laddar' : 'Ladda fler'}
            onClick={() => loadMoreItems()}
            isLoading={loadMore}
          />
        </div>
      ) : null}
    </div>
  ) : null;
};

export default ShipVisitHistory;
